<template>
  <v-card>
    <v-card-title
      class="primary white--text py-1 my-0 title-sticky d-print-none"
    >
      {{ title }}
      <v-spacer></v-spacer>
      <v-btn
        v-if="hasApprovalPanel"
        small
        tile
        color="secondary"
        class="mx-1 approval-toggle"
        :aria-expanded="String(approvalOpen)"
        :aria-controls="approvalPanelId"
        @click="toggleApprovalPanel"
      >
        <v-badge
          dot
          overlap
          color="warning"
          :value="approvalPending"
          class="mr-2"
        >
          <v-icon small>mdi-timeline-text-outline</v-icon>
        </v-badge>
        <span class="approval-toggle__label">
          {{ approvalOpen ? t('收起审批') : t('审批流程') }}
        </span>
      </v-btn>
      <slot name="titlebtns">
        <v-btn
          @click="closeAndTo(backRouteName, {}, {})"
          color="secondary"
          small
          v-if="needBack"
          tile
          class="mx-1"
        >
          <v-icon left dark>mdi-format-list-bulleted-square</v-icon>
          {{ t('返回列表') }}
        </v-btn>
        <v-btn
          v-if="canSave && !hideBtn"
          width="60"
          tile
          @click="save"
          color="success"
          small
          class="mx-1"
          :loading="loading"
        >
          {{ t(saveText || '保存') }}
        </v-btn>
        <v-btn
          v-if="canSubmit && !hideBtn"
          width="80"
          tile
          @click="submit"
          color="success"
          small
          class="mx-1"
          :loading="sloading"
        >
          {{ t(submitText || '保存并提交') }}
        </v-btn>
        <v-btn
          v-if="canPrint"
          width="60"
          tile
          @click="printElement(title)"
          color="primary"
          small
          class="mx-1"
        >
          {{ t('打印') }}
        </v-btn>
        <slot name="custombtns"></slot>
      </slot>
    </v-card-title>
    <div
      class="detail-view-body"
      :class="{
        'detail-view-body--approval-ready': hasApprovalPanel,
        'detail-view-body--with-approval': hasApprovalPanel && approvalOpen,
      }"
      @keydown.esc="closeApprovalPanel"
    >
      <main class="detail-view-main">
        <slot name="main-topcontent"></slot>
        <div v-for="(s, i) in subtitles" :key="s">
          <v-card-title class="h6 mt-1 ml-1 left-border">
            {{ subtitleI18n && subtitleI18n[i] ? subtitleI18n[i] : s }}
            <!-- 显示多语言 -->
            <v-spacer></v-spacer>
            <slot :name="`${s}按钮`"></slot>
          </v-card-title>
          <slot :name="s"></slot>
          <!-- 保持插槽名称为原始 key -->
          <v-divider></v-divider>
        </div>
        <slot></slot>
      </main>
      <transition name="approval-panel">
        <aside
          v-show="hasApprovalPanel && approvalOpen"
          :id="approvalPanelId"
          ref="approvalAside"
          class="approval-aside"
          tabindex="-1"
          role="complementary"
          :aria-label="t('审批流程')"
        >
          <div class="approval-aside__toolbar d-print-none">
            <div class="approval-aside__heading">
              <v-icon color="primary" class="mr-2">
                mdi-timeline-check-outline
              </v-icon>
              <span>{{ t('审批流程') }}</span>
              <v-chip
                x-small
                class="ml-2"
                :color="approvalPending ? 'warning' : 'secondary'"
                :outlined="!approvalPending"
              >
                {{ approvalPending ? t('待审批') : t('流程记录') }}
              </v-chip>
            </div>
            <v-btn
              icon
              small
              :aria-label="t('收起审批')"
              @click="closeApprovalPanel"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
          <div
            class="approval-aside__content"
            tabindex="0"
            role="region"
            :aria-label="t('审批内容')"
          >
            <slot name="topcontent"></slot>
          </div>
        </aside>
      </transition>
      <transition name="approval-backdrop">
        <div
          v-show="hasApprovalPanel && approvalOpen"
          class="approval-backdrop d-print-none"
          aria-hidden="true"
          @click="closeApprovalPanel"
        ></div>
      </transition>
    </div>
    <iframe
      ref="printFrame"
      style="visibility: hidden; position: absolute"
    ></iframe>
  </v-card>
</template>
<script>
import routerControl from '@/mixin/routerControl'
import print from '@/mixin/print'
import approvalPanel from '@/mixin/approvalPanel'
export default {
  name: 'v-detail-view',
  mixins: [routerControl, print, approvalPanel],
  props: {
    title: String,
    backRouteName: String,
    subtitles: Array,
    subtitleI18n: Array,
    tooltip: String,
    saveText: String,
    submitText: String,
    canSubmit: [Boolean, String],
    canSave: {
      type: Boolean,
      default: true,
    },
    needBack: {
      type: Boolean,
      default: true,
    },
    canPrint: [Boolean, String],
  },
  data() {
    return {
      hideBtn: false,
      loading: false,
      sloading: false,
      lang: this.getLangFromCookie(),
      messages: {
        zh: {},
        en: {},
      },
    }
  },

  watch: {
    tooltip(tooltip) {
      this.$store.commit('updateViewTagsTooltip', {
        tooltip,
        nowFullPath: this.$route.path,
      })
    },
  },

  methods: {
    getLangFromCookie() {
      const values = String(document.cookie || '')
        .split(';')
        .map((c) => c.trim())
        .filter((c) => c.startsWith('language='))
        .map((c) => c.substring('language='.length))
      const lang = values.length ? values[values.length - 1] : 'zh'
      return lang === 'en' ? 'en' : 'zh'
    },
    t(key) {
      return this.messages[this.lang]?.[key] || key
    },
    async emitPromise(method, ...params) {
      let listener =
        this.$listeners[method] || this.$attrs[method] || this[method]
      if (listener) {
        //one can additionally wrap this in try/catch if needed and handle the error further
        let res = await listener(...params)
        return res === undefined || res
      }
      return false
    },
    async save() {
      this.loading = true
      try {
        await this.emitPromise('save', () => {
          this.closeAndTo(this.backRouteName)
        })
      } finally {
        this.loading = false
      }
    },
    async submit() {
      this.sloading = true
      try {
        await this.emitPromise('submit', () => {
          this.closeAndTo(this.backRouteName)
        })
      } finally {
        this.sloading = false
      }
    },
    updateTooltip(tooltip) {
      this.$store.commit('updateViewTagsTooltip', {
        tooltip,
        nowFullPath: this.$route.path,
      })
    },
  },
  created() {
    this.updateTooltip(this.tooltip)
  },
}
</script>

<style>
.left-border {
  position: relative;
}
.left-border::before {
  content: '';
  width: 5px;
  height: 30px;
  position: absolute;
  top: 25%;
  left: 0;
  background: #3399cc;
}
.title-sticky {
  position: sticky;
  top: 85px;
  z-index: 3;
}
</style>
